import useCreateMutation from '../../libs/hooks/useCreateMutation';
import CREATE_ELIGIBILITY_WIDGET_TOKEN, {
  type CreateEligibilityWidgetTokenRequest,
  type CreateEligibilityWidgetTokenResponse,
  defaultEligibilityWidgetResponse,
} from '../queries/CREATE_ELIGIBILITY_WIDGET_TOKEN';
import type {
  WidgetOffer,
  WidgetOfferLink,
  WidgetSegment,
} from '../../../types/eligibilityWidget/EligibilityWidgetToken';
import useEligibilityWidgetStore from './useEligibilityWidgetStore';
import type EligibilityWidgetFormData from '../../../types/eligibilityWidget/EligibilityWidgetFormData';
import useTranslation from '../../states/translation/useTranslation';
import useCurrentLanguage from '../../states/language/useCurrentLanguage';
import { useLocation } from 'react-router-dom';
import useAppConfig from '../../states/config/useAppConfig';
import useCurrentCurrency from '../../states/currency/useCurrentCurrency';
import useFormatCurrency from '../../libs/utils/currency/useFormatCurrency';

const useEligibilityWidget = () => {
  const {
    states: { pid, apiKey },
    actions: { setPid, setApiKey },
  } = useEligibilityWidgetStore();
  const { initialized: textKeysLoaded } = useTranslation().states;
  const { t } = useTranslation().actions;
  const { currentLanguage } = useCurrentLanguage().states;
  const { currentCurrency } = useCurrentCurrency().states;
  const { formatAmountWithCurrency } = useFormatCurrency().actions;
  const { getCurrencies } = useAppConfig().selectors;
  const {
    states,
    actions: { create: createToken },
  } = useCreateMutation<CreateEligibilityWidgetTokenResponse, CreateEligibilityWidgetTokenRequest>({
    query: CREATE_ELIGIBILITY_WIDGET_TOKEN,
    defaultResponse: defaultEligibilityWidgetResponse,
  });
  const location = useLocation();
  const { data, loading, called, reset } = states;
  const idle = !called;
  const showForm = idle;
  const isEligible = data?.createWidgetToken?.eligible ?? false;
  const segments = data?.createWidgetToken?.segments ?? [];
  const tokenCurrencyCode = data?.createWidgetToken?.currency ?? '';

  const getTokenCurrency = () => getCurrencies().find((c) => c.code === tokenCurrencyCode);

  /**
   * Override will be detected if @pnr && @lastName are present in the query params
   */
  const detectOverrideMode = () => {
    const searchParams = new URLSearchParams(location.search);
    const pnr = searchParams.get('pnr') ?? '';
    const lastName = searchParams.get('lastName') ?? '';
    const affiliatePid = searchParams.get('affiliatePid') ?? '';
    const affiliateApiKey = searchParams.get('affiliateApiKey') ?? '';
    const overrideMode = Boolean(pnr && lastName);

    return {
      pnr,
      lastName,
      overrideMode,
      affiliatePid,
      affiliateApiKey,
    };
  };

  /**
   * Eligibility Widget will auto-submit if override is detected
   * For OTAs @affiliatePid && @affiliateApiKey must be provided
   */
  const autoSubmit = () => {
    const { overrideMode, pnr, lastName, affiliatePid, affiliateApiKey } = detectOverrideMode();

    if (overrideMode) {
      createToken({
        pid: affiliatePid ?? pid,
        apiKey: affiliateApiKey ?? apiKey,
        pnr,
        lastName,
      }).catch(console.error);
    }
  };

  const onSubmit = async ({ pnr, lastName, pid, apiKey }: EligibilityWidgetFormData) =>
    await createToken({
      pid,
      apiKey,
      pnr,
      lastName,
    });

  const getEligibilityWidgetTokenProps = () => {
    return {
      isEligible,
      offers: getOffers(segments, currentLanguage.code),
      links: getLinks(segments, currentLanguage.code),
      openLinks,
      resetApp,
    };
  };

  const getEligibilityWidgetFormProps = () => {
    return {
      pid,
      apiKey,
      onSubmit,
    };
  };

  const getUpgradeButtonLabel = (offer: WidgetOffer) =>
    offer.price
      ? t(
          'eligibility-widget.offers.label',
          offer.journey.originCode,
          offer.journey.destinationCode,
          t(`common.offer.upgrade.type.${offer.textKey}`),
          formatAmountWithCurrency(offer.price, getTokenCurrency() ?? currentCurrency),
        )
      : t('eligibility-widget.offers.label.no-price', offer.journey.originCode, offer.journey.destinationCode);

  const resetApp = () => reset();

  return {
    states: {
      showForm,
      loading,
      textKeysLoaded,
    },
    selectors: {
      getEligibilityWidgetTokenProps,
      getEligibilityWidgetFormProps,
      getUpgradeButtonLabel,
    },
    actions: {
      resetApp,
      setPid,
      setApiKey,
      createToken,
      autoSubmit,
      detectOverrideMode,
    },
  };
};

export const getOffers = (segments: WidgetSegment[], language: string): WidgetOffer[] =>
  segments.flatMap(({ journey, upgradeTypes }: WidgetSegment) =>
    upgradeTypes.map(({ upgradeType, overallStartingPrice, submitUrl, textKey }) => {
      return {
        journey,
        upgradeType,
        price: overallStartingPrice,
        submitUrl: appendLanguageToUrl(submitUrl, language),
        textKey,
      };
    }),
  );

export const getLinks = (segments: WidgetSegment[], language: string): WidgetOfferLink[] =>
  segments?.flatMap(({ journey, upgradeTypes }: WidgetSegment) =>
    upgradeTypes.map(({ upgradeType, submitUrl }) => {
      return {
        id: `${journey.originCode}${journey.destinationCode}${upgradeType}`,
        url: appendLanguageToUrl(submitUrl, language),
      };
    }),
  ) ?? [];

const openLinks = (links: WidgetOfferLink[]) =>
  links.filter(removeDuplicateLinks).forEach((link) => {
    window.open(link.url, link.id);
  });

const removeDuplicateLinks = (obj: { url: string }, index: number, links: WidgetOfferLink[]) =>
  links.findIndex((link) => link.url === obj.url) === index;

const appendLanguageToUrl = (url: string, language: string) => (language ? `${url}?lang=${language}` : url);

export default useEligibilityWidget;
